import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"
import { phoneComplete, formattedPhone, isImageFile, acceptedPhotoTypes, PHOTO_CROP_SLOT } from "../lib/thank_you_preview_fields"

// Step 6 (onboarding/thank_you_page, #12341): the live "what your leads will
// see" card and the Continue-button gate for the Dynamic Thank-You Page step.
//
// Gate — Continue is disabled until all three hold (OnboardingStepService#
// save_thank_you_page enforces the first two server-side; the photo is never a
// server-side block):
//   1. Title has non-whitespace input.
//   2. Phone normalizes to 10 digits (a leading US 1 is tolerated).
//   3. A photo is present — already on the profile (hasPhoto value), selected in
//      this session (the shared photo-crop slot re-fires `change` on the input
//      with the cropped file, or with EMPTY files on a cancelled crop) — OR the
//      agent chose "I'll add my photo later", which writes skip_photo=1.
//
// Preview — title, phone (formatted), NPN row and the calendar CTA mirror the
// inputs on every keystroke; a selected photo replaces the card's avatar.
//
// Wire: data-controller="thank-you-step"
//       data-thank-you-step-has-photo-value="true|false"
//       text inputs: data-action="input->thank-you-step#refresh" + a *Input target
//       file input:  change->thank-you-step#photoChanged (via the slot's extra_input_actions)
//       skip button: data-action="thank-you-step#skipPhoto"
export default class extends Controller {
  static targets = ["titleInput", "phoneInput", "npnInput", "calendarInput",
    "skipPhotoInput", "skipPhotoBtn", "skipPhotoNote", "continueBtn", "statusMessage",
    "previewPhoto", "previewTitle", "previewPhone", "previewNpnRow", "previewNpn", "previewCalendarRow"]
  // titleMaxLength mirrors OnboardingStepService::THANK_YOU_TITLE_MAX_LENGTH
  // (the server rule and the input's maxlength); the default matches it.
  static values = { hasPhoto: Boolean, titleMaxLength: { type: Number, default: 80 } }

  static EMPTY_CLASS = "gl-ty-preview__empty"
  static TITLE_PLACEHOLDER = "Your title"
  static PHONE_PLACEHOLDER = "Your phone number"

  connect() {
    this.photoSelected = false
    // Generation counter for the async avatar read (same pattern as
    // photo_crop_controller's this.session): a FileReader that resolves after
    // the crop was cancelled must not paint the discarded photo.
    this.previewSession = 0
    this.photoSkipped = this.hasSkipPhotoInputTarget && this.skipPhotoInputTarget.value === "1"
    this.initialPreviewPhotoHTML = this.hasPreviewPhotoTarget ? this.previewPhotoTarget.innerHTML : ""
    this.refresh()
  }

  refresh() {
    this.renderPreview()
    this.renderGate()
  }

  photoChanged(event) {
    const file = event.target.files?.[0]
    // `accept` filters the picker, it does not constrain it — a PDF or a GIF
    // chosen via "All Files" would otherwise enable Continue while photo-crop
    // shows its type error and the PATCH 422s. Only a type the slot accepts
    // counts — read off the slot itself, so the gate, the crop modal and the
    // server (PhotoValidation, JPEG/PNG + HEIC the crop converts) stay aligned.
    this.photoSelected = this.isImage(file)
    // Every change starts a new preview generation, so an earlier read that is
    // still in flight (select, cancel the crop quickly) is discarded on arrival.
    this.previewSession += 1
    if (this.photoSelected) {
      this.showPreviewPhoto(file)
    } else if (this.hasPreviewPhotoTarget) {
      this.previewPhotoTarget.innerHTML = this.initialPreviewPhotoHTML
    }
    // A chosen photo retires the "add it later" offer; a cancelled crop (empty
    // files) brings it back. An already-taken skip stays hidden either way.
    if (this.hasSkipPhotoBtnTarget && !this.photoSkipped) this.skipPhotoBtnTarget.hidden = this.photoSelected
    this.renderGate()
  }

  skipPhoto() {
    this.photoSkipped = true
    if (this.hasSkipPhotoInputTarget) this.skipPhotoInputTarget.value = "1"
    if (this.hasSkipPhotoBtnTarget) this.skipPhotoBtnTarget.hidden = true
    if (this.hasSkipPhotoNoteTarget) this.skipPhotoNoteTarget.hidden = false
    this.renderGate()
  }

  // ── Preview ────────────────────────────────────────────────────────
  renderPreview() {
    this.renderText(this.hasPreviewTitleTarget && this.previewTitleTarget, this.titleValue(),
      this.constructor.TITLE_PLACEHOLDER)
    this.renderText(this.hasPreviewPhoneTarget && this.previewPhoneTarget, this.formattedPhone(),
      this.constructor.PHONE_PLACEHOLDER)

    const npn = this.inputValue("npnInput")
    if (this.hasPreviewNpnTarget) this.previewNpnTarget.textContent = npn
    if (this.hasPreviewNpnRowTarget) this.previewNpnRowTarget.hidden = npn.length === 0
    if (this.hasPreviewCalendarRowTarget) {
      this.previewCalendarRowTarget.hidden = this.inputValue("calendarInput").length === 0
    }
  }

  renderText(element, value, placeholder) {
    if (!element) return
    element.textContent = value || placeholder
    element.classList.toggle(this.constructor.EMPTY_CLASS, !value)
  }

  // photo-crop's accepted list decides here (shared rule: lib/thank_you_preview_fields).
  isImage(file) {
    return isImageFile(file, this.acceptedPhotoTypes())
  }

  // photo-crop's own accepted list — the slot sits inside this step's root —
  // falling back to its default when the slot is not on the page.
  acceptedPhotoTypes() {
    return acceptedPhotoTypes(this.element.querySelector(PHOTO_CROP_SLOT))
  }

  showPreviewPhoto(file) {
    if (!this.hasPreviewPhotoTarget || !this.isImage(file)) return
    const session = this.previewSession
    const reader = new FileReader()
    reader.onload = (e) => {
      // A later photoChanged (cancelled crop, or another file) superseded this read.
      if (session !== this.previewSession) return
      const img = document.createElement("img")
      img.src = e.target.result
      img.alt = "Your photo"
      img.className = "gl-ty-preview__img"
      this.previewPhotoTarget.replaceChildren(img)
    }
    reader.readAsDataURL(file)
  }

  // ── Gate ───────────────────────────────────────────────────────────
  renderGate() {
    const titleLength = this.titleValue().length
    const titleTooLong = titleLength > this.titleMaxLengthValue
    const titleOk = titleLength > 0 && !titleTooLong
    const phoneOk = phoneComplete(this.inputValue("phoneInput"))
    const photoOk = this.hasPhotoValue || this.photoSelected || this.photoSkipped
    const npnOk = this.npnDigitsOnly()
    const calendarOk = this.calendarLinkOk()
    const ready = titleOk && phoneOk && photoOk && npnOk && calendarOk
    const message = ready ? "" : this.gateMessage({ titleOk, titleTooLong, phoneOk, photoOk, npnOk, calendarOk })

    if (this.hasContinueBtnTarget) {
      // The reason rides the button as its tooltip + composed accessible
      // name (the shared onboarding gate contract, lib/gate_reason.js); the
      // status line below stays the visible copy of it. It is not also wired
      // through aria-describedby — the name already carries the reason, so a
      // description would announce it a second time (#13439).
      syncGateReason(this.continueBtnTarget, { ready, reason: message })
    }

    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent = message
      this.statusMessageTarget.classList.toggle("d-none", ready)
    }
  }

  // Mirrors the server's first-failure copy (OnboardingStepService#thank_you_page_error),
  // in its order: an over-long title and a non-digit NPN are their own messages,
  // everything missing is one "Add … to continue."
  gateMessage({ titleOk, titleTooLong, phoneOk, photoOk, npnOk, calendarOk }) {
    if (titleTooLong) return `Title must be ${this.titleMaxLengthValue} characters or fewer.`
    if (!titleOk || !phoneOk || !photoOk) return this.missingMessage(titleOk, phoneOk, photoOk)
    if (!npnOk) return "NPN must contain only digits."
    if (!calendarOk) return "Please enter a valid calendar link (for example, calendly.com/you)."
    return ""
  }

  // The calendar link is optional; when present it must parse as a URL with a
  // dotted host once https:// is assumed — the same shape the server's
  // UrlFormatChecker accepts after with_https_scheme (#12804 review).
  calendarLinkOk() {
    const value = this.inputValue("calendarInput")
    if (value.length === 0) return true
    if (/\s/.test(value)) return false
    try {
      const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`)
      // Same host shape as UrlFormatChecker (char, dot, two chars).
      return /^https?:$/.test(url.protocol) && /.\.../.test(url.hostname)
    } catch {
      return false
    }
  }

  // The NPN is optional; when present it is digits only (display_npn), the same
  // rule the server applies — otherwise the styled status line would stay silent
  // while the input's native pattern blocked the submit.
  npnDigitsOnly() {
    return /^\d*$/.test(this.inputValue("npnInput"))
  }

  missingMessage(titleOk, phoneOk, photoOk) {
    const missing = []
    if (!titleOk) missing.push("your title")
    if (!phoneOk) missing.push("a 10-digit phone number for leads to call")
    if (!photoOk) missing.push("a photo (or choose I'll add my photo later)")
    return `Add ${this.joinList(missing)} to continue.`
  }

  joinList(items) {
    if (items.length <= 1) return items.join("")
    return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
  }

  // ── Input readers ──────────────────────────────────────────────────
  inputValue(targetName) {
    const has = this[`has${targetName[0].toUpperCase()}${targetName.slice(1)}Target`]
    return has ? this[`${targetName}Target`].value.trim() : ""
  }

  titleValue() {
    return this.inputValue("titleInput")
  }

  formattedPhone() {
    return formattedPhone(this.inputValue("phoneInput"))
  }
}
